import axios from "axios";

// En dev, le backend tourne sur un port séparé (pas de proxy) donc on cible
// 127.0.0.1:8000 explicitement. En prod, REACT_APP_API_URL est inliné au
// build (ex: https://pouletdirect.bf/api) et Caddy route /api vers le backend.
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

const API = axios.create({
  baseURL: API_BASE_URL,
});

// Intercepteur pour inclure le token d'authentification
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthEndpoint = error.config?.url?.includes("/users/login") || error.config?.url?.includes("/users/register");
    // Un 401 sur /login ou /register est une réponse normale (mauvais
    // identifiants) que le formulaire affiche lui-même — ne pas déconnecter
    // ni rediriger, sinon la page se recharge avant même que l'erreur
    // s'affiche. Un 401 ailleurs signifie que la session en cours a expiré.
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (window.location.pathname !== "/login") {
        sessionStorage.setItem("session_expired", "1");
        window.location.href = "/login";
      }
    }
    // FastAPI renvoie un detail sous forme de liste d'objets (pas une
    // chaîne) sur une erreur de validation 422. Le code applicatif fait
    // partout `err.response?.data?.detail || "..."` et affiche le résultat
    // tel quel dans du JSX — un tableau d'objets y provoque un crash de
    // rendu React ("Objects are not valid as a React child"), qui écrase
    // silencieusement le formulaire sans le moindre message. On normalise
    // ici une fois pour toutes en une chaîne lisible.
    const detail = error.response?.data?.detail;
    if (Array.isArray(detail)) {
      error.response.data.detail = detail
        .map((e) => (typeof e === "string" ? e : e.msg))
        .filter(Boolean)
        .join(" ; ");
    }
    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  register: (data) => API.post("/users/register", data),
  login: (data) => {
    const formData = new FormData();
    formData.append("username", data.email);
    formData.append("password", data.password);
    return API.post("/users/login", formData);
  },
  getProfile: () => API.get("/users/me"),
  updateProfile: (data) => API.put("/users/me", data),
  getUser: (id) => API.get(`/users/${id}`),
  forgotPassword: (email) => API.post("/users/forgot-password", { email }),
  resetPassword: (token, new_password) => API.post("/users/reset-password", { token, new_password }),
};

export const productsAPI = {
  getAll: (params) => API.get("/products", { params }),
  getOne: (id) => API.get(`/products/${id}`),
  create: (data) => API.post("/products", data),
  update: (id, data) => API.put(`/products/${id}`, data),
  delete: (id) => API.delete(`/products/${id}`),
  getMyProducts: () => API.get("/products/my-products"),
  getCategories: () => API.get("/products/categories/list"),
};

export const ordersAPI = {
  getAll: (params) => API.get("/orders", { params }),
  getOne: (id) => API.get(`/orders/${id}`),
  create: (data) => API.post("/orders", data),
  update: (id, data) => API.put(`/orders/${id}`, data),
  delete: (id) => API.delete(`/orders/${id}`),
  addTracking: (id, data) => API.post(`/orders/${id}/tracking`, data),
  ship: (id, data) => API.post(`/orders/${id}/ship`, data),
  verifyDelivery: (id, data) => API.post(`/orders/${id}/verify-delivery`, data),
  getDeliveryOTP: (id) => API.get(`/orders/${id}/delivery-otp`),
  estimateDeliveryPrice: (params) => API.get("/orders/delivery-price/estimate", { params }),
};

export const reviewsAPI = {
  create: (data) => API.post("/reviews", data),
  getUserReviews: (userId, params) => API.get(`/reviews/user/${userId}`, { params }),
  getOne: (id) => API.get(`/reviews/${id}`),
  checkForOrder: (orderId) => API.get(`/reviews/order/${orderId}/check`),
  delete: (id) => API.delete(`/reviews/${id}`),
};

export const messagesAPI = {
  getAll: (params) => API.get("/messages", { params }),
  getConversations: () => API.get("/messages/conversations"),
  getOne: (id) => API.get(`/messages/${id}`),
  getContact: (userId) => API.get(`/messages/contact/${userId}`),
  create: (data) => API.post("/messages", data),
  markAsRead: (id) => API.put(`/messages/${id}/read`),
};

export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return API.post("/upload/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  getImageUrl: (filename) => `${API_ORIGIN}/api/upload/image/${filename}`,
};

export const statsAPI = {
  getBreederStats: (params) => API.get("/stats/breeder", { params }),
  getRestaurantStats: (params) => API.get("/stats/restaurant", { params }),
  getCommissionRates: () => API.get("/stats/commission-rates"),
};

export const paymentsAPI = {
  initiate: (orderId, data) => API.post(`/payments/initiate/${orderId}`, data),
  verify: (orderId) => API.get(`/payments/verify/${orderId}`),
  getStatus: (orderId) => API.get(`/payments/status/${orderId}`),
  simulate: (orderId) => API.post(`/payments/simulate/${orderId}`),
};

export const notificationsAPI = {
  getCounts: () => API.get("/notifications/counts"),
  getAll: (params) => API.get("/notifications", { params }),
  markAsRead: (id) => API.put(`/notifications/${id}/read`),
  markAllAsRead: () => API.put("/notifications/read-all"),
  getActions: () => API.get("/notifications/actions"),
};

export default API;
